import { useEffect, useState } from "react";
import * as Yup from "yup";
import * as moment from "moment";

import { getContractors } from "common/services/contractors.service";
import { getPersons } from "common/services/persons.service";
import { getAgencies } from "common/services/agencies.service";
import { normalize } from "common/lib/normalize";
import { Option } from "./fields";

/** Small helpers shared by the record kinds of every profile. */

export const initials = (name?: string) =>
  (name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "?";

export const toDate = (value: any) => (value ? new Date(value) : null);

/** Calendar dates go over the wire as YYYY-MM-DD in local time. */
export const toApiDate = (value: any) =>
  value instanceof Date ? value.toLocaleDateString("en-CA") : value || null;

export const period = (from: any, to: any) => {
  if (!from && !to) return "";
  const start = from ? moment(from).format("MMM YYYY") : "Unknown start";
  const end = to ? moment(to).format("MMM YYYY") : "present";
  return `${start} – ${end}`;
};

export const joinMeta = (...parts: Array<string | null | undefined>) =>
  parts.filter((part) => part && part.trim()).join(" · ");

export const option = (id: any, label?: string): Option | null =>
  id ? { value: id, label: label || "" } : null;

export const endAfterStart = (startField: string) =>
  Yup.date()
    .nullable()
    .test("after-start", "End date is before the start date", function (end) {
      const start = this.parent[startField];
      return !start || !end || new Date(end) >= new Date(start);
    });

/** Searches persons by name; `excludeId` leaves out the person being edited. */
export const loadPersons =
  (excludeId?: any) =>
  async (input: string): Promise<Option[]> => {
    const response: any = await getPersons({
      pagination: { page: 1, pageSize: 20 },
      sort: ["fullName:asc"],
      fields: ["fullName"],
      filters: {
        ...(excludeId ? { id: { $ne: excludeId } } : {}),
        ...(input ? { fullName: { $containsi: input } } : {}),
      },
    });
    return normalize(response.data).map((person: any) => ({
      value: person.id,
      label: person.fullName || "Unnamed person",
    }));
  };

/** Searches contractors by name; `excludeId` leaves out the one being edited. */
export const loadContractors =
  (excludeId?: any) =>
  async (input: string): Promise<Option[]> => {
    const response: any = await getContractors({
      pagination: { page: 1, pageSize: 20 },
      sort: ["contractorName:asc"],
      fields: ["contractorName"],
      filters: {
        ...(excludeId ? { id: { $ne: excludeId } } : {}),
        ...(input ? { contractorName: { $containsi: input } } : {}),
      },
    });
    return normalize(response.data).map((contractor: any) => ({
      value: contractor.id,
      label: contractor.contractorName,
    }));
  };

/** Loads a short reference list once per session and shares it. */
export const useReferenceOptions = (load: () => Promise<Option[]>, cache: { current: Option[] | null }) => {
  const [options, setOptions] = useState<Option[]>(cache.current || []);
  const [loading, setLoading] = useState(!cache.current);
  useEffect(() => {
    if (cache.current) return;
    let active = true;
    load()
      .then((loaded) => {
        cache.current = loaded;
        if (active) setOptions(loaded);
      })
      .catch(() => {})
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [cache, load]);
  return { options, loading };
};

export const agencyCache: { current: Option[] | null } = { current: null };
export const loadAgencies = async () => {
  const response: any = await getAgencies({
    pagination: { page: 1, pageSize: 10000 },
    sort: ["name:asc"],
    filters: { category: { $null: false } },
  });
  return normalize(response.data).map((agency: any) => ({
    value: agency.id,
    label: agency.name,
  }));
};
